import React, { useEffect } from 'react';
import styled from 'styled-components';
import { vibrateAchievement } from '../utils/haptics';
import { Die } from '../types/game';
import { MaxiYatzyScores } from '../types/maxi';
import { maxiScoreCategories, calculateMaxiScore } from '../utils/maxiGameLogic';
import { useToast } from '../context/ToastContext';
import { useT } from '../i18n';
import {
  ScoreCardContainer as BaseScoreCardContainer,
  SectionTitle,
  ScoreRow,
  CategoryInfo,
  StatusIcon,
  CategoryName,
  CategoryDescription,
  ScoreValue,
  SummaryRow,
  TotalScore,
} from './shared/ScoreCardStyles';

interface MaxiScoreCardProps {
  scores: MaxiYatzyScores;
  dice: Die[];
  onScoreSelect: (category: keyof MaxiYatzyScores) => void;
  rollsLeft: number;
  upperSectionScore: number;
  upperSectionBonus: number;
  totalScore: number;
  highlightCategory?: string | null;
  forcedOrder?: (keyof MaxiYatzyScores)[];
  keyboardNav?: boolean;
  /** Antal kast per omgång i aktuellt läge (default 3). */
  maxRolls?: number;
}

/* Maxi variant has more categories → different scroll breakpoints */
const ScoreCardContainer = styled(BaseScoreCardContainer)`
  @media (min-width: 768px) and (max-height: 1000px) {
    max-height: min(calc(100vh - 170px), 720px);
  }
  @media (min-width: 768px) and (orientation: landscape) and (max-height: 600px) {
    max-height: calc(100vh - 130px);
    padding: 24px 24px 20px;
  }
`;

const PointingHandIcon = styled.span`
  position: absolute;
  left: -12px;
  font-size: 20px;
  opacity: 0.9;
  animation: pulse 1.5s ease-in-out infinite;
  
  @keyframes pulse {
    0%, 100% { opacity: 0.8; transform: scale(1); }
    50% { opacity: 1; transform: scale(1.15); }
  }
`;

const MaxiScoreCard = React.forwardRef<HTMLDivElement, MaxiScoreCardProps>(({ 
  scores,
  dice,
  onScoreSelect,
  rollsLeft,
  upperSectionScore,
  upperSectionBonus,
  totalScore,
  highlightCategory,
  forcedOrder,
  keyboardNav = true,
  maxRolls = 3
}, ref) => {
  const { showConfirmationToast } = useToast();
  const t = useT();
  // Defensive: ensure all undefined are coerced to null to avoid appearing as 0 / completed
  const safeScores = React.useMemo(() => {
    // Ensure every expected key exists (undefined -> null) so UI treats as empty/selectable
    const clone: any = { ...scores };
    maxiScoreCategories.forEach(cat => {
      if (clone[cat.key] === undefined) clone[cat.key] = null;
    });
    return clone as MaxiYatzyScores;
  }, [scores]);
  const bonusRef = React.useRef(false);
  useEffect(() => {
    if (!bonusRef.current && upperSectionBonus > 0) {
      bonusRef.current = true;
      try { vibrateAchievement(); } catch {}
    }
  }, [upperSectionBonus]);
  const upper = maxiScoreCategories.filter(c => c.isUpperSection);
  const lower = maxiScoreCategories.filter(c => !c.isUpperSection);
  // CHANGE (2026-08-15): Härlett från maxRolls i stället för hårdkodat 3 – i
  // Maxi-multiplayer (fler kast) blev annars ingen kategori valbar.
  const canSelect = rollsLeft < maxRolls;
  const nextForcedKey = React.useMemo(()=> {
    if(!forcedOrder) return null;
    return forcedOrder.find(k => scores[k] === null) || null;
  }, [forcedOrder, scores]);

  const renderSection = (cats: typeof maxiScoreCategories, title: string) => (
    <div>
      <SectionTitle>{title}</SectionTitle>
    {cats.map(cat => {
  const currentScore = safeScores[cat.key];
  const isEmpty = currentScore === null;
  const isSelectable = canSelect && isEmpty && (!nextForcedKey || nextForcedKey === cat.key);
  const safeDice = dice || [] as any;
  const possible = isEmpty ? calculateMaxiScore(safeDice, cat.key) : 0;
    const isStruck = !isEmpty && currentScore === 0;
    const isCompleted = !isEmpty && (currentScore ?? 0) > 0;
    // CHANGE (2026-02-23): I tvingande ordning ska handen visas även vid 0-poäng
    // för att tydligt markera att kategorin måste väljas/kan strykas.
    const isForcedPick = !!nextForcedKey;
    const shouldShowHand = isSelectable && isEmpty && (possible > 0 || isForcedPick);
    return (
          <ScoreRow
            key={cat.key as string}
            $isSelectable={isSelectable}
            $isSelected={false}
            $isStruck={isStruck}
            $isCompleted={isCompleted}
            $shouldShowHand={shouldShowHand}
            $highlight={highlightCategory === cat.key}
            onClick={() => {
              if(!isSelectable) return;
              if (calculateMaxiScore(dice as any, cat.key) === 0) {
                showConfirmationToast(t('game.strikeConfirm', { category: t(`cat.${String(cat.key)}.name`) }), {
                  onConfirm: () => onScoreSelect(cat.key),
                  confirmText: t('game.strikeYes'),
                  cancelText: t('game.strikeNo'),
                  replaceKey: 'strike-confirmation'
                });
                return;
              }
              onScoreSelect(cat.key);
            }}
            tabIndex={keyboardNav && isSelectable ? 0 : -1}
            onKeyDown={(e) => {
              if (!keyboardNav) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (isSelectable) {
                  if (calculateMaxiScore(dice as any, cat.key) === 0) {
                    showConfirmationToast(t('game.strikeConfirm', { category: t(`cat.${String(cat.key)}.name`) }), {
                      onConfirm: () => onScoreSelect(cat.key),
                      confirmText: t('game.strikeYes'),
                      cancelText: t('game.strikeNo'),
                      replaceKey: 'strike-confirmation'
                    });
                  } else {
                    onScoreSelect(cat.key);
                  }
                }
              } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                const focusable = Array.from((ref as any)?.current?.querySelectorAll('[data-score-row="1"]') || []) as HTMLElement[];
                const idx = focusable.indexOf(e.currentTarget as HTMLElement);
                if (idx >= 0) {
                  const nextIdx = e.key === 'ArrowDown' ? Math.min(focusable.length -1, idx +1) : Math.max(0, idx -1);
                  focusable[nextIdx]?.focus();
                }
              }
            }}
            data-score-row={isSelectable ? '1' : undefined}
          >
            {shouldShowHand && (
              <PointingHandIcon role="img" aria-label="Möjligt val">👉</PointingHandIcon>
            )}
            <CategoryInfo>
              <CategoryName>{t(`cat.${String(cat.key)}.name`)}</CategoryName>
              <CategoryDescription>{t(`cat.${String(cat.key)}.desc`)}</CategoryDescription>
            </CategoryInfo>
            <ScoreValue $isPossible={possible > 0} $dim={isEmpty && (!isSelectable || possible === 0)}>
              {/* CHANGE (2026-08-15): Visa (0) på valbara rader så stryk-kandidater syns direkt. */}
              {currentScore !== null ? currentScore : isSelectable ? `(${possible})` : '–'}
            </ScoreValue>
            {(() => {
              if (currentScore === null) return null;
              if (currentScore === 0) return <StatusIcon $variant="cross" aria-label="Nollad">✕</StatusIcon>;
              const upperKeys: Record<string, number> = { ones:1, twos:2, threes:3, fours:4, fives:5, sixes:6 };
              if (upperKeys[cat.key]) {
                const fv = upperKeys[cat.key];
                const count = currentScore / fv;
                if (count >= 4) return <StatusIcon $variant="star" aria-label="Hög träff">★</StatusIcon>;
                if (count === 3) return <StatusIcon $variant="check" aria-label="Tre av ett tal">✓</StatusIcon>;
                return <StatusIcon $variant="cross" aria-label="Mindre än tre">✕</StatusIcon>;
              }
              // Define max theoretical scores for maxi categories (approximate where needed)
              const maxMap: Record<string, number> = {
                onePair:12, twoPairs:22, threeOfAKind:18, fourOfAKind:24, fiveOfAKind:30,
                smallStraight:15, largeStraight:20, fullStraight:21, hus:(6*3 +5*3), torn:(6*4 +5*2), chance:36, yatzy:100
              };
              const max = maxMap[cat.key as string];
              if (max) {
                if (currentScore >= 0.8 * max) return <StatusIcon $variant="star" aria-label="Topppoäng">★</StatusIcon>;
                return <StatusIcon $variant="check" aria-label="Klar">✓</StatusIcon>;
              }
              return null;
            })()}
          </ScoreRow>
        );
      })}
    </div>
  );

  return (
  <ScoreCardContainer ref={ref}>
      {renderSection(upper, t('score.upper'))}

      <SummaryRow>
        <span>{t('score.upperSum')}</span>
        <span style={{display:'flex',alignItems:'center',gap:8}}>
          {upperSectionScore}
          {(() => {
            const threshold = 84;
            // Remaining potential (treat each unfilled upper category as max 5 of that face)
            const remainingUpper = maxiScoreCategories.filter(c=>c.isUpperSection && scores[c.key] === null);
            const faceVal: Record<string, number> = { ones:1, twos:2, threes:3, fours:4, fives:5, sixes:6 };
            const maxRemain = remainingUpper.reduce((s,c)=> s + faceVal[c.key]*6, 0);
            if (upperSectionScore >= threshold) return <span role="img" aria-label="Bonus uppnådd" style={{fontSize:24,filter:'drop-shadow(0 2px 3px rgba(0,0,0,.55))'}}>⭐</span>;
            if (upperSectionScore + maxRemain < threshold) return <span role="img" aria-label="Bonus omöjlig" style={{fontSize:22,filter:'drop-shadow(0 2px 3px rgba(0,0,0,.45))'}}>😢</span>;
            return <span role="img" aria-label="Bonus fortfarande möjlig" style={{fontSize:22,filter:'drop-shadow(0 2px 3px rgba(0,0,0,.45))'}}>🙂</span>;
          })()}
        </span>
      </SummaryRow>

      <SummaryRow>
        <span>{t('score.bonusRowMaxi')}</span>
        <span>{upperSectionBonus}</span>
      </SummaryRow>

      {renderSection(lower, t('score.lower'))}

      {forcedOrder && nextForcedKey && (
        <div style={{margin:'4px 0 10px',fontSize:12,background:'#F1F8E9',padding:'6px 10px',borderRadius:6}}>
          {t('score.forcedNext')} <strong>{maxiScoreCategories.find(c=>c.key===nextForcedKey)?.name}</strong>
        </div>
      )}
      <TotalScore>
        {t('score.total', { total: totalScore })}
      </TotalScore>
  </ScoreCardContainer>
  );
});

export default MaxiScoreCard;
